import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import {
  clientExchange,
  ClientExchangeParams,
  ClientExchangeResponse,
} from "../api/dumontApi";
import { DUMONT_TOKEN_KEY, DUMONT_DEFAULT_AGENT_ID_KEY } from "../constants";
import env from "../util/env";

interface DumontContextValue {
  token: string | null;
  isLoading: boolean;
  error: string | null;
  exchange: (
    params: Omit<ClientExchangeParams, "clientIdentifier">
  ) => Promise<ClientExchangeResponse | null>;
  clearToken: () => void;
  baseUrl: string;
  defaultAgentId: string | null;
}

const DumontContext = createContext<DumontContextValue | null>(null);

export function DumontProvider({ children }: { children: React.ReactNode }) {
  const baseUrl = env.DUMONT_AI_URL || "";
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultAgentId, setDefaultAgentId] = useState<string | null>(null);

  const setToken = useCallback((value: string | null) => {
    setTokenState(value);
    if (typeof sessionStorage !== "undefined") {
      if (value) sessionStorage.setItem(DUMONT_TOKEN_KEY, value);
      else sessionStorage.removeItem(DUMONT_TOKEN_KEY);
    }
  }, []);

  const clearToken = useCallback(() => {
    setTokenState(null);
    setError(null);
    setDefaultAgentId(null);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(DUMONT_TOKEN_KEY);
      sessionStorage.removeItem(DUMONT_DEFAULT_AGENT_ID_KEY);
    }
  }, []);

  const exchange = useCallback(
    async (
      params: Omit<ClientExchangeParams, "clientIdentifier">
    ): Promise<ClientExchangeResponse | null> => {
      if (!baseUrl) {
        setError("Dumont AI URL is not configured");
        return null;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await clientExchange(baseUrl, {
          email: params.email,
        });
        setToken(result.token);
        const nextDefaultAgentId = result.defaultAgentId ?? null;
        setDefaultAgentId(nextDefaultAgentId);
        if (typeof sessionStorage !== "undefined") {
          if (nextDefaultAgentId) {
            sessionStorage.setItem(DUMONT_DEFAULT_AGENT_ID_KEY, nextDefaultAgentId);
          } else {
            sessionStorage.removeItem(DUMONT_DEFAULT_AGENT_ID_KEY);
          }
        }
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Ask Dumont is unavailable";
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, setToken]
  );

  const value: DumontContextValue = {
    token,
    isLoading,
    error,
    exchange,
    clearToken,
    baseUrl,
    defaultAgentId,
  };

  return <DumontContext.Provider value={value}>{children}</DumontContext.Provider>;
}

export function useDumont(): DumontContextValue {
  const ctx = useContext(DumontContext);
  if (!ctx) {
    throw new Error("useDumont must be used within DumontProvider");
  }
  return ctx;
}